import * as THREE from 'three';
import { Random } from '../core/Random';
import { DART_ORDER, DART_RINGS } from '../minigames/darts';
import { fillRich, richWidth } from '../icons/canvas';

/**
 * Procedural canvas textures. Everything in the game is generated at runtime, so the
 * build ships zero image assets. Textures are cached by key and shared.
 */

const cache = new Map<string, THREE.CanvasTexture>();

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas unsupported');
  return [c, ctx];
}

function finish(c: HTMLCanvasElement, opts: { repeat?: boolean; srgb?: boolean } = {}): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  if (opts.srgb !== false) tex.colorSpace = THREE.SRGBColorSpace;
  if (opts.repeat !== false) tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  return tex;
}

function cached(key: string, make: () => THREE.CanvasTexture): THREE.CanvasTexture {
  let t = cache.get(key);
  if (!t) {
    t = make();
    cache.set(key, t);
  }
  return t;
}

/**
 * Text is baked into canvases, so a texture drawn before its web font arrived would keep
 * the fallback font for good (boot only waits so long for the font CDN). Repaint it once
 * the font shows up.
 */
function repaintWhenFontsLoad(fonts: readonly string[], tex: THREE.Texture, paint: () => void): void {
  if (typeof document === 'undefined' || !('fonts' in document)) return;
  const missing = [...new Set(fonts)].filter((f) => !document.fonts.check(`16px "${f}"`));
  if (missing.length === 0) return;
  Promise.all(missing.map((f) => document.fonts.load(`16px "${f}"`)))
    .then(() => {
      paint();
      tex.needsUpdate = true;
    })
    .catch(() => undefined);
}

function speckle(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  n: number,
  colors: string[],
  rng: Random,
  size = 2,
): void {
  for (let i = 0; i < n; i++) {
    ctx.fillStyle = rng.pick(colors);
    const s = rng.range(0.5, size);
    ctx.fillRect(rng.range(0, w), rng.range(0, h), s, s);
  }
}

/** Neutral luminance detail for grass; hue comes from vertex colors on the ground. */
export function grassTexture(): THREE.CanvasTexture {
  return cached('grass', () => {
    const [c, ctx] = makeCanvas(256, 256);
    const rng = new Random(11);
    ctx.fillStyle = '#d8d8d8';
    ctx.fillRect(0, 0, 256, 256);
    for (let i = 0; i < 2600; i++) {
      const v = rng.int(170, 255);
      ctx.strokeStyle = `rgb(${v},${v},${v})`;
      ctx.lineWidth = rng.range(0.6, 1.6);
      const x = rng.range(0, 256);
      const y = rng.range(0, 256);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + rng.range(-2, 2), y - rng.range(3, 8));
      ctx.stroke();
    }
    speckle(ctx, 256, 256, 400, ['#9a9a9a', '#b5b5b5'], rng, 2.5);
    return finish(c);
  });
}

export function gravelTexture(): THREE.CanvasTexture {
  return cached('gravel', () => {
    const [c, ctx] = makeCanvas(256, 256);
    const rng = new Random(12);
    ctx.fillStyle = '#9b8f7e';
    ctx.fillRect(0, 0, 256, 256);
    speckle(ctx, 256, 256, 5000, ['#857a6b', '#b3a892', '#6f665a', '#c9bfa9', '#7d7466'], rng, 3.5);
    return finish(c);
  });
}

export function asphaltTexture(): THREE.CanvasTexture {
  return cached('asphalt', () => {
    const [c, ctx] = makeCanvas(256, 256);
    const rng = new Random(13);
    ctx.fillStyle = '#3d3f42';
    ctx.fillRect(0, 0, 256, 256);
    speckle(ctx, 256, 256, 6000, ['#2f3134', '#4a4c50', '#56585c', '#36383b'], rng, 2);
    ctx.strokeStyle = 'rgba(20,20,20,0.6)';
    for (let i = 0; i < 6; i++) {
      ctx.lineWidth = rng.range(0.8, 1.6);
      ctx.beginPath();
      let x = rng.range(0, 256);
      let y = rng.range(0, 256);
      ctx.moveTo(x, y);
      for (let j = 0; j < 6; j++) {
        x += rng.range(-18, 18);
        y += rng.range(-18, 18);
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    return finish(c);
  });
}

/** Highway with a dashed yellow center line; v runs across the road. */
export function highwayTexture(): THREE.CanvasTexture {
  return cached('highway', () => {
    const [c, ctx] = makeCanvas(256, 256);
    ctx.drawImage(asphaltTexture().image as HTMLCanvasElement, 0, 0);
    ctx.fillStyle = '#e7c22c';
    for (let x = 0; x < 256; x += 64) ctx.fillRect(x, 124, 36, 8);
    ctx.fillStyle = '#e8e8e2';
    ctx.fillRect(0, 10, 256, 5);
    ctx.fillRect(0, 241, 256, 5);
    return finish(c);
  });
}

/** Vertical ribbed aluminum siding, grayscale so it can be tinted per trailer. */
export function sidingTexture(): THREE.CanvasTexture {
  return cached('siding', () => {
    const [c, ctx] = makeCanvas(128, 128);
    const g = ctx.createLinearGradient(0, 0, 0, 128);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(0.85, '#f1f1f1');
    g.addColorStop(1, '#d6d2c8');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    for (let x = 0; x < 128; x += 16) {
      ctx.fillStyle = 'rgba(0,0,0,0.13)';
      ctx.fillRect(x, 0, 2, 128);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillRect(x + 2, 0, 1, 128);
    }
    return finish(c);
  });
}

export function rustySidingTexture(): THREE.CanvasTexture {
  return cached('siding-rust', () => {
    const [c, ctx] = makeCanvas(128, 128);
    ctx.drawImage(sidingTexture().image as HTMLCanvasElement, 0, 0);
    const rng = new Random(21);
    for (let i = 0; i < 9; i++) {
      const x = rng.range(0, 128);
      const len = rng.range(30, 110);
      const g = ctx.createLinearGradient(0, 0, 0, len);
      g.addColorStop(0, 'rgba(140,70,20,0.55)');
      g.addColorStop(1, 'rgba(140,70,20,0)');
      ctx.fillStyle = g;
      ctx.fillRect(x, 0, rng.range(2, 6), len);
    }
    return finish(c);
  });
}

export function roofTexture(): THREE.CanvasTexture {
  return cached('roof', () => {
    const [c, ctx] = makeCanvas(128, 128);
    ctx.fillStyle = '#c9c9c4';
    ctx.fillRect(0, 0, 128, 128);
    for (let y = 0; y < 128; y += 8) {
      ctx.fillStyle = 'rgba(0,0,0,0.12)';
      ctx.fillRect(0, y, 128, 2);
    }
    const rng = new Random(31);
    speckle(ctx, 128, 128, 300, ['rgba(90,60,30,0.3)', 'rgba(60,60,60,0.25)'], rng, 4);
    return finish(c);
  });
}

export function latticeTexture(): THREE.CanvasTexture {
  return cached('lattice', () => {
    const [c, ctx] = makeCanvas(128, 128);
    ctx.fillStyle = '#2a2622';
    ctx.fillRect(0, 0, 128, 128);
    ctx.strokeStyle = '#f2efe6';
    ctx.lineWidth = 7;
    for (let i = -128; i < 256; i += 32) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + 128, 128);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(i + 128, 0);
      ctx.lineTo(i, 128);
      ctx.stroke();
    }
    return finish(c);
  });
}

export function chainLinkTexture(): THREE.CanvasTexture {
  return cached('chainlink', () => {
    const [c, ctx] = makeCanvas(64, 64);
    ctx.clearRect(0, 0, 64, 64);
    ctx.strokeStyle = '#b8bcc0';
    ctx.lineWidth = 2.2;
    for (let i = -64; i < 128; i += 16) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + 64, 64);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(i + 64, 0);
      ctx.lineTo(i, 64);
      ctx.stroke();
    }
    return finish(c);
  });
}

export function woodTexture(): THREE.CanvasTexture {
  return cached('wood', () => {
    const [c, ctx] = makeCanvas(128, 128);
    ctx.fillStyle = '#9c7a52';
    ctx.fillRect(0, 0, 128, 128);
    const rng = new Random(41);
    for (let y = 0; y < 128; y += 16) {
      ctx.fillStyle = `rgba(${rng.int(60, 90)},${rng.int(40, 60)},20,0.25)`;
      ctx.fillRect(0, y, 128, 16);
      ctx.fillStyle = 'rgba(40,25,10,0.6)';
      ctx.fillRect(0, y, 128, 1.5);
      for (let i = 0; i < 5; i++) {
        ctx.strokeStyle = 'rgba(60,40,20,0.25)';
        ctx.beginPath();
        const yy = y + rng.range(3, 13);
        ctx.moveTo(0, yy);
        ctx.bezierCurveTo(40, yy + rng.range(-3, 3), 80, yy + rng.range(-3, 3), 128, yy);
        ctx.stroke();
      }
    }
    return finish(c);
  });
}

export function camoTexture(): THREE.CanvasTexture {
  return cached('camo', () => {
    const [c, ctx] = makeCanvas(128, 128);
    const rng = new Random(51);
    ctx.fillStyle = '#6b7346';
    ctx.fillRect(0, 0, 128, 128);
    const colors = ['#3f4a2a', '#8a7a52', '#2e2a1f', '#556b2f'];
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = rng.pick(colors);
      ctx.beginPath();
      const x = rng.range(0, 128);
      const y = rng.range(0, 128);
      ctx.ellipse(x, y, rng.range(6, 18), rng.range(4, 10), rng.range(0, Math.PI), 0, Math.PI * 2);
      ctx.fill();
    }
    return finish(c);
  });
}

export function plaidTexture(base: string): THREE.CanvasTexture {
  return cached(`plaid-${base}`, () => {
    const [c, ctx] = makeCanvas(64, 64);
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    for (let i = 0; i < 64; i += 16) {
      ctx.fillRect(i, 0, 7, 64);
      ctx.fillRect(0, i, 64, 7);
    }
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    for (let i = 10; i < 64; i += 16) {
      ctx.fillRect(i, 0, 1.5, 64);
      ctx.fillRect(0, i, 64, 1.5);
    }
    return finish(c);
  });
}

export function leopardTexture(): THREE.CanvasTexture {
  return cached('leopard', () => {
    const [c, ctx] = makeCanvas(128, 128);
    const rng = new Random(61);
    ctx.fillStyle = '#d9a441';
    ctx.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 46; i++) {
      const x = rng.range(0, 128);
      const y = rng.range(0, 128);
      const r = rng.range(4, 8);
      ctx.fillStyle = '#7a4a1a';
      ctx.beginPath();
      ctx.ellipse(x, y, r, r * 0.8, rng.range(0, 3), 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#1c120a';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(x, y, r + 1, rng.range(0, 3), rng.range(3.5, 6));
      ctx.stroke();
    }
    return finish(c);
  });
}

export function americanFlagTexture(): THREE.CanvasTexture {
  return cached('flag-us', () => {
    const [c, ctx] = makeCanvas(190, 100);
    for (let i = 0; i < 13; i++) {
      ctx.fillStyle = i % 2 === 0 ? '#b22234' : '#ffffff';
      ctx.fillRect(0, (i * 100) / 13, 190, 100 / 13 + 0.5);
    }
    ctx.fillStyle = '#3c3b6e';
    ctx.fillRect(0, 0, 76, 54);
    ctx.fillStyle = '#ffffff';
    for (let r = 0; r < 9; r++) {
      const cols = r % 2 === 0 ? 6 : 5;
      for (let col = 0; col < cols; col++) {
        const x = 6.3 + col * 12.6 + (r % 2 === 0 ? 0 : 6.3);
        ctx.beginPath();
        ctx.arc(x, 4 + r * 5.6, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    return finish(c, { repeat: false });
  });
}

export function checkeredFlagTexture(): THREE.CanvasTexture {
  return cached('flag-check', () => {
    const [c, ctx] = makeCanvas(96, 64);
    for (let y = 0; y < 4; y++)
      for (let x = 0; x < 6; x++) {
        ctx.fillStyle = (x + y) % 2 === 0 ? '#111' : '#f4f4f4';
        ctx.fillRect(x * 16, y * 16, 16, 16);
      }
    return finish(c, { repeat: false });
  });
}

export function foilTexture(): THREE.CanvasTexture {
  return cached('foil', () => {
    const [c, ctx] = makeCanvas(64, 64);
    const rng = new Random(71);
    ctx.fillStyle = '#c9ccd1';
    ctx.fillRect(0, 0, 64, 64);
    for (let i = 0; i < 70; i++) {
      const v = rng.int(150, 250);
      ctx.fillStyle = `rgb(${v},${v},${v + 4})`;
      ctx.beginPath();
      ctx.moveTo(rng.range(0, 64), rng.range(0, 64));
      ctx.lineTo(rng.range(0, 64), rng.range(0, 64));
      ctx.lineTo(rng.range(0, 64), rng.range(0, 64));
      ctx.fill();
    }
    return finish(c);
  });
}

export function cardboardTexture(text = ''): THREE.CanvasTexture {
  return cached(`cardboard-${text}`, () => {
    const [c, ctx] = makeCanvas(128, 128);
    const paint = (): void => {
      const rng = new Random(81);
      ctx.fillStyle = '#b98d55';
      ctx.fillRect(0, 0, 128, 128);
      speckle(ctx, 128, 128, 500, ['#a77c48', '#c79c62'], rng, 2);
      ctx.strokeStyle = 'rgba(90,60,30,0.5)';
      ctx.strokeRect(6, 6, 116, 116);
      if (text) {
        ctx.fillStyle = '#151515';
        ctx.font = '22px "Permanent Marker", cursive';
        ctx.textAlign = 'center';
        wrapText(ctx, text, 64, 50, 112, 24);
      }
    };
    paint();
    const tex = finish(c);
    if (text) repaintWhenFontsLoad(['Permanent Marker'], tex, paint);
    return tex;
  });
}

export function blindsTexture(): THREE.CanvasTexture {
  return cached('blinds', () => {
    const [c, ctx] = makeCanvas(64, 64);
    ctx.fillStyle = '#efe6cf';
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    for (let y = 0; y < 64; y += 5) ctx.fillRect(0, y, 64, 1.5);
    // The classic one bent slat somebody peeks through.
    ctx.fillStyle = '#2a2a2a';
    ctx.fillRect(22, 30, 18, 3);
    return finish(c);
  });
}

export function glassTexture(): THREE.CanvasTexture {
  return cached('glass', () => {
    const [c, ctx] = makeCanvas(64, 64);
    const g = ctx.createLinearGradient(0, 0, 64, 64);
    g.addColorStop(0, '#9fc3d6');
    g.addColorStop(0.45, '#40606f');
    g.addColorStop(1, '#22333d');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath();
    ctx.moveTo(8, 0);
    ctx.lineTo(20, 0);
    ctx.lineTo(0, 22);
    ctx.lineTo(0, 10);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.fillRect(31, 0, 2, 64);
    return finish(c, { repeat: false });
  });
}

export function rustTexture(): THREE.CanvasTexture {
  return cached('rust', () => {
    const [c, ctx] = makeCanvas(128, 128);
    const rng = new Random(91);
    ctx.fillStyle = '#7b4a2a';
    ctx.fillRect(0, 0, 128, 128);
    speckle(ctx, 128, 128, 2500, ['#5e3520', '#9a5b30', '#4a2c1c', '#a8693a', '#6d6d6d'], rng, 4);
    return finish(c);
  });
}

export function poolWallTexture(): THREE.CanvasTexture {
  return cached('poolwall', () => {
    const [c, ctx] = makeCanvas(128, 64);
    ctx.fillStyle = '#2f7fc4';
    ctx.fillRect(0, 0, 128, 64);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 4;
    ctx.beginPath();
    for (let x = 0; x <= 128; x += 4) ctx.lineTo(x, 30 + Math.sin((x / 128) * Math.PI * 4) * 8);
    ctx.stroke();
    ctx.fillStyle = '#d7dde2';
    ctx.fillRect(0, 0, 128, 6);
    ctx.fillRect(0, 58, 128, 6);
    return finish(c);
  });
}

export function waterTexture(): THREE.CanvasTexture {
  return cached('water', () => {
    const [c, ctx] = makeCanvas(128, 128);
    ctx.fillStyle = '#4fb3d9';
    ctx.fillRect(0, 0, 128, 128);
    const rng = new Random(101);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    for (let i = 0; i < 40; i++) {
      ctx.lineWidth = rng.range(1, 2.5);
      const x = rng.range(0, 128);
      const y = rng.range(0, 128);
      ctx.beginPath();
      ctx.arc(x, y, rng.range(4, 12), rng.range(0, 3), rng.range(3, 5));
      ctx.stroke();
    }
    return finish(c);
  });
}

export function corkBoardTexture(): THREE.CanvasTexture {
  return cached('cork', () => {
    const [c, ctx] = makeCanvas(256, 160);
    const paint = (): void => {
      const rng = new Random(111);
      ctx.fillStyle = '#b5844f';
      ctx.fillRect(0, 0, 256, 160);
      speckle(ctx, 256, 160, 1500, ['#9b6e3e', '#c99a63', '#8a5f34'], rng, 2.5);
      ctx.strokeStyle = '#5a3a1e';
      ctx.lineWidth = 10;
      ctx.strokeRect(0, 0, 256, 160);
      ctx.fillStyle = '#151515';
      ctx.font = '22px "Permanent Marker", cursive';
      ctx.textAlign = 'center';
      ctx.fillText('ODD JOBS', 128, 30);
      const notes = ['MOW LAWN $$', 'HAUL JUNK', 'LOST CAT', 'BASS BOAT 4 SALE', 'GUITAR LESSONS'];
      notes.forEach((n, i) => {
        const x = 16 + (i % 3) * 80;
        const y = 44 + Math.floor(i / 3) * 56;
        ctx.save();
        ctx.translate(x + 34, y + 24);
        ctx.rotate(rng.range(-0.15, 0.15));
        ctx.fillStyle = rng.pick(['#fff8b0', '#ffffff', '#ffd1e8', '#d6f0ff']);
        ctx.fillRect(-34, -24, 68, 48);
        ctx.fillStyle = '#d33';
        ctx.beginPath();
        ctx.arc(0, -19, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#222';
        ctx.font = '10px "Permanent Marker", cursive';
        // Centered on the note below the pin, wrapped so nothing hangs off the edge.
        const lines = wrapLines(ctx, n, 62);
        lines.forEach((line, k) => ctx.fillText(line, 0, 8 + (k - (lines.length - 1) / 2) * 12));
        ctx.restore();
      });
    };
    paint();
    const tex = finish(c, { repeat: false });
    repaintWhenFontsLoad(['Permanent Marker'], tex, paint);
    return tex;
  });
}

/** Greedy word wrap at the context's current font. */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const w of text.split(' ')) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lh: number,
): void {
  wrapLines(ctx, text, maxW).forEach((line, i) => ctx.fillText(line, x, y + i * lh));
}

export interface SignLine {
  text: string;
  size: number;
  font?: string;
  color?: string;
  weight?: string;
}

export interface SignSpec {
  w: number;
  h: number;
  bg: string;
  fg?: string;
  border?: string;
  borderWidth?: number;
  lines: SignLine[];
  grime?: boolean;
  gap?: number;
}

/** Renders a text sign. Fonts must already be loaded (see main.ts). */
export function signTexture(spec: SignSpec): THREE.CanvasTexture {
  const key = `sign-${JSON.stringify(spec)}`;
  return cached(key, () => {
    const [c, ctx] = makeCanvas(spec.w, spec.h);
    const paint = (): void => {
      ctx.clearRect(0, 0, spec.w, spec.h);
      ctx.fillStyle = spec.bg;
      ctx.fillRect(0, 0, spec.w, spec.h);
      if (spec.border) {
        const bw = spec.borderWidth ?? 8;
        ctx.strokeStyle = spec.border;
        ctx.lineWidth = bw;
        ctx.strokeRect(bw / 2, bw / 2, spec.w - bw, spec.h - bw);
      }
      const gap = spec.gap ?? 8;
      const total = spec.lines.reduce((sum, l) => sum + l.size, 0) + gap * (spec.lines.length - 1);
      let y = (spec.h - total) / 2;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (const line of spec.lines) {
        ctx.fillStyle = line.color ?? spec.fg ?? '#111';
        ctx.font = `${line.weight ?? ''} ${line.size}px "${line.font ?? 'Rye'}", serif`.trim();
        let size = line.size;
        while (richWidth(ctx, line.text, size) > spec.w * 0.92 && size > 8) {
          size -= 2;
          ctx.font = `${line.weight ?? ''} ${size}px "${line.font ?? 'Rye'}", serif`.trim();
        }
        // Text with `:icon:` tokens draws the icons inline.
        fillRich(ctx, line.text, spec.w / 2, y + (line.size - size) / 2, size);
        y += line.size + gap;
      }
      if (spec.grime) {
        const rng = new Random(spec.w * 31 + spec.h);
        speckle(
          ctx,
          spec.w,
          spec.h,
          Math.floor((spec.w * spec.h) / 90),
          ['rgba(60,40,20,0.18)', 'rgba(0,0,0,0.12)'],
          rng,
          3,
        );
        const g = ctx.createLinearGradient(0, spec.h * 0.6, 0, spec.h);
        g.addColorStop(0, 'rgba(80,50,20,0)');
        g.addColorStop(1, 'rgba(80,50,20,0.25)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, spec.w, spec.h);
      }
    };
    paint();
    const tex = finish(c, { repeat: false });
    repaintWhenFontsLoad(
      spec.lines.map((l) => l.font ?? 'Rye'),
      tex,
      paint,
    );
    return tex;
  });
}

/** Soft round sprite used by particles and glows. */
export function glowTexture(): THREE.CanvasTexture {
  return cached('glow', () => {
    const [c, ctx] = makeCanvas(64, 64);
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.8)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    return finish(c, { repeat: false });
  });
}

export function tornadoTexture(): THREE.CanvasTexture {
  return cached('tornado', () => {
    const [c, ctx] = makeCanvas(128, 256);
    const rng = new Random(131);
    ctx.fillStyle = 'rgba(70,72,70,0.55)';
    ctx.fillRect(0, 0, 128, 256);
    for (let i = 0; i < 90; i++) {
      ctx.fillStyle = `rgba(${rng.int(40, 120)},${rng.int(40, 120)},${rng.int(40, 110)},${rng.range(0.15, 0.5)})`;
      ctx.fillRect(rng.range(0, 128), rng.range(0, 256), rng.range(10, 60), rng.range(2, 8));
    }
    return finish(c);
  });
}

/** 1970s wood paneling: vertical boards with dark grooves. */
export function panelingTexture(): THREE.CanvasTexture {
  return cached('paneling', () => {
    const [c, ctx] = makeCanvas(128, 128);
    const rng = new Random(71);
    ctx.fillStyle = '#7a5332';
    ctx.fillRect(0, 0, 128, 128);
    for (let x = 0; x < 128; x += 32) {
      ctx.fillStyle = `rgba(${rng.int(90, 130)},${rng.int(55, 80)},${rng.int(25, 40)},0.55)`;
      ctx.fillRect(x, 0, 32, 128);
      for (let i = 0; i < 7; i++) {
        ctx.strokeStyle = 'rgba(45,25,10,0.3)';
        ctx.beginPath();
        const xx = x + rng.range(3, 29);
        ctx.moveTo(xx, 0);
        ctx.bezierCurveTo(xx + rng.range(-3, 3), 40, xx + rng.range(-3, 3), 90, xx, 128);
        ctx.stroke();
      }
      ctx.fillStyle = 'rgba(25,14,6,0.8)';
      ctx.fillRect(x, 0, 2, 128);
    }
    return finish(c);
  });
}

/** Grandma's wallpaper: pale stripes and little roses. */
export function floralTexture(): THREE.CanvasTexture {
  return cached('floral', () => {
    const [c, ctx] = makeCanvas(128, 128);
    const rng = new Random(73);
    ctx.fillStyle = '#efe3c8';
    ctx.fillRect(0, 0, 128, 128);
    ctx.fillStyle = 'rgba(190,160,120,0.25)';
    for (let x = 0; x < 128; x += 16) ctx.fillRect(x, 0, 6, 128);
    for (let i = 0; i < 18; i++) {
      const x = (i % 6) * 21 + (Math.floor(i / 6) % 2) * 10 + 6;
      const y = Math.floor(i / 6) * 43 + 14;
      ctx.fillStyle = '#6f8f55';
      ctx.beginPath();
      ctx.ellipse(x + 4, y + 5, 4, 2, 0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = rng.pick(['#c75b6b', '#d9828f', '#b5485a']);
      ctx.beginPath();
      ctx.arc(x, y, 4.5, 0, Math.PI * 2);
      ctx.fill();
    }
    return finish(c);
  });
}

/** Shag carpet in any color you like, as long as it's from 1977. */
export function shagTexture(color: string): THREE.CanvasTexture {
  return cached(`shag-${color}`, () => {
    const [c, ctx] = makeCanvas(128, 128);
    const rng = new Random(79);
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 128, 128);
    speckle(
      ctx,
      128,
      128,
      2600,
      ['rgba(0,0,0,0.18)', 'rgba(255,255,255,0.12)', 'rgba(60,30,10,0.15)'],
      rng,
      3,
    );
    // A stain nobody talks about.
    ctx.fillStyle = 'rgba(70,40,20,0.12)';
    ctx.beginPath();
    ctx.ellipse(80, 70, 18, 11, 0.4, 0, Math.PI * 2);
    ctx.fill();
    return finish(c);
  });
}

/** Checkered linoleum, a little yellowed. */
export function linoleumTexture(): THREE.CanvasTexture {
  return cached('linoleum', () => {
    const [c, ctx] = makeCanvas(64, 64);
    for (let y = 0; y < 4; y++)
      for (let x = 0; x < 4; x++) {
        ctx.fillStyle = (x + y) % 2 ? '#3f3a34' : '#e8dfc6';
        ctx.fillRect(x * 16, y * 16, 16, 16);
      }
    ctx.fillStyle = 'rgba(160,130,60,0.12)';
    ctx.fillRect(0, 0, 64, 64);
    return finish(c);
  });
}

/** Racing-theme wallpaper: a checkered band over a red pinstripe. Tiles. */
export function racingWallTexture(): THREE.CanvasTexture {
  return cached('racing-wall', () => {
    const [c, ctx] = makeCanvas(64, 64);
    ctx.fillStyle = '#f1efe8';
    ctx.fillRect(0, 0, 64, 64);
    for (let x = 0; x < 8; x++)
      for (let y = 0; y < 2; y++) {
        ctx.fillStyle = (x + y) % 2 ? '#141414' : '#f4f4f4';
        ctx.fillRect(x * 8, 24 + y * 8, 8, 8);
      }
    ctx.fillStyle = '#c62828';
    ctx.fillRect(0, 44, 64, 4);
    return finish(c);
  });
}

/** Fraction of the dartboard texture's radius that is the scoring area (the rest is numbers). */
export const DARTBOARD_SCORING = 0.765;

/** A regulation-looking dartboard: the same rings and segments the darts game scores by. */
export function dartboardTexture(): THREE.CanvasTexture {
  return cached('dartboard', () => {
    const S = 512;
    const [c, g] = makeCanvas(S, S);
    const cx = S / 2;
    const rad = (S / 2) * DARTBOARD_SCORING;
    g.fillStyle = '#141414';
    g.beginPath();
    g.arc(cx, cx, S / 2, 0, Math.PI * 2);
    g.fill();
    const R = DART_RINGS;
    for (let i = 0; i < 20; i++) {
      const a0 = -Math.PI / 2 - Math.PI / 20 + (i * Math.PI) / 10;
      const a1 = a0 + Math.PI / 10;
      const dark = i % 2 === 0;
      const ring = (r0: number, r1: number, color: string): void => {
        g.fillStyle = color;
        g.beginPath();
        g.arc(cx, cx, r1 * rad, a0, a1);
        g.arc(cx, cx, r0 * rad, a1, a0, true);
        g.closePath();
        g.fill();
      };
      ring(R.outerBull, R.doubleOut, dark ? '#1b1b1b' : '#efe3c6');
      ring(R.tripleIn, R.tripleOut, dark ? '#c62828' : '#2f7d32');
      ring(R.doubleIn, R.doubleOut, dark ? '#c62828' : '#2f7d32');
      // Wire between the segments.
      g.strokeStyle = '#b8b8b8';
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(cx + Math.cos(a0) * R.outerBull * rad, cx + Math.sin(a0) * R.outerBull * rad);
      g.lineTo(cx + Math.cos(a0) * rad, cx + Math.sin(a0) * rad);
      g.stroke();
      g.fillStyle = '#f2efe6';
      g.font = 'bold 34px Anton, Impact, sans-serif';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      const am = a0 + Math.PI / 20;
      const nr = rad + (S / 2 - rad) / 2;
      g.fillText(String(DART_ORDER[i]), cx + Math.cos(am) * nr, cx + Math.sin(am) * nr);
    }
    g.fillStyle = '#2f7d32';
    g.beginPath();
    g.arc(cx, cx, R.outerBull * rad, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#c62828';
    g.beginPath();
    g.arc(cx, cx, R.bull * rad, 0, Math.PI * 2);
    g.fill();
    return finish(c, { repeat: false });
  });
}
